import { useQuery } from '@tanstack/react-query';
import { EditIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { SelectFormField, type SelectOption } from '@/components/common/FormField';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import type { Project } from '../data/databaseSchema';
import { handleApiError } from '../lib/errors';
import { employeeService } from '../services/employeeService';
import { projectService } from '../services/projectService';
import {
  employeesForProjectRoleSelect,
  projectRoleValueMatchesEmployee,
} from '../utils/employeeDisplay';
import { logger } from '../utils/logger';

function buildEmployeeRoleOptions(
  employees: ReturnType<typeof employeesForProjectRoleSelect>,
  currentValue: string,
  allEmployees: Awaited<ReturnType<typeof employeeService.getEmployees>>
): SelectOption[] {
  const options: SelectOption[] = [{ value: '', label: 'None' }];
  if (currentValue.trim() !== '' && !projectRoleValueMatchesEmployee(allEmployees, currentValue)) {
    options.push({ value: currentValue, label: `${currentValue.trim()} (current)` });
  }
  for (const e of employees) {
    options.push({ value: e.employee_id, label: e.fullname });
  }
  return options;
}

interface ProjectRolesFormData {
  manager: string;
  assistant: string;
  deputy: string;
  finance: string;
}

function rolesFromProject(project: Project): ProjectRolesFormData {
  return {
    manager: project.manager ?? '',
    assistant: project.assistant ?? '',
    deputy: project.deputy ?? '',
    finance: project.finance ?? '',
  };
}

interface ProjectRolesPanelProps {
  project: Project;
  onClose: () => void;
  onSaved?: (project: Project) => void;
}

export const ProjectRolesPanel: React.FC<ProjectRolesPanelProps> = ({
  project,
  onClose,
  onSaved,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProjectRolesFormData>(() => rolesFromProject(project));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: employeeService.getEmployees,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    setFormData(rolesFromProject(project));
    setIsEditing(false);
    setSubmitError(null);
  }, [project]);

  const managerOptions = useMemo(
    () =>
      buildEmployeeRoleOptions(
        employeesForProjectRoleSelect(employees, formData.manager),
        formData.manager,
        employees
      ),
    [employees, formData.manager]
  );
  const assistantOptions = useMemo(
    () =>
      buildEmployeeRoleOptions(
        employeesForProjectRoleSelect(employees, formData.assistant),
        formData.assistant,
        employees
      ),
    [employees, formData.assistant]
  );
  const deputyOptions = useMemo(
    () =>
      buildEmployeeRoleOptions(
        employeesForProjectRoleSelect(employees, formData.deputy),
        formData.deputy,
        employees
      ),
    [employees, formData.deputy]
  );
  const financeOptions = useMemo(
    () =>
      buildEmployeeRoleOptions(
        employeesForProjectRoleSelect(employees, formData.finance),
        formData.finance,
        employees
      ),
    [employees, formData.finance]
  );

  const handleCancelEdit = () => {
    setFormData(rolesFromProject(project));
    setSubmitError(null);
    setIsEditing(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await projectService.updateProject(project.id, {
        project_number: project.project_number,
        name: project.name,
        description: project.description ?? undefined,
        type: project.type ?? undefined,
        portfolio: project.portfolio ?? undefined,
        process_technology: project.process_technology ?? undefined,
        delivery_model: project.delivery_model ?? undefined,
        start_date: project.start_date,
        end_date: project.end_date,
        status: project.status,
        manager: formData.manager.trim() || undefined,
        assistant: formData.assistant.trim() || undefined,
        deputy: formData.deputy.trim() || undefined,
        finance: formData.finance.trim() || undefined,
      });
      onSaved?.(response);
      setIsEditing(false);
    } catch (error) {
      logger.error('Error saving project roles:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleFields = (
    [
      ['Manager', 'manager', managerOptions],
      ['Assistant', 'assistant', assistantOptions],
      ['Deputy', 'deputy', deputyOptions],
      ['Finance', 'finance', financeOptions],
    ] as const
  ).map(([label, field, options]) => ({ label, field, options }));

  const body = (
    <FieldGroup>
      {roleFields.map(({ label, field, options }) => (
        <SelectFormField
          key={field}
          label={label}
          value={formData[field]}
          onChange={(v) => setFormData((prev) => ({ ...prev, [field]: v }))}
          options={options}
          disabled={!isEditing}
          copyable={!isEditing}
        />
      ))}
    </FieldGroup>
  );

  const footer = isEditing ? (
    <div className="flex shrink-0 justify-end gap-2 border-t border-border-default px-4 py-3">
      <Button type="button" variant="outline" onClick={handleCancelEdit} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? <Spinner data-icon="inline-start" /> : null}
        {isSubmitting ? 'Saving...' : 'Save'}
      </Button>
    </div>
  ) : (
    <div className="flex shrink-0 justify-end border-t border-border-default px-4 py-3">
      <Button type="button" variant="outline" onClick={onClose}>
        Close
      </Button>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border-default px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Project roles</h2>
            <p className="mt-1 text-sm text-text-secondary">
              {project.name} ({project.project_number ?? '—'})
            </p>
          </div>
          {!isEditing ? (
            <Button type="button" size="sm" onClick={() => setIsEditing(true)}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
        </div>
      </div>
      {isEditing ? (
        <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">{body}</div>
          {submitError ? (
            <Alert variant="destructive" className="mx-4 mb-4">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}
          {footer}
        </form>
      ) : (
        <>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">{body}</div>
          {footer}
        </>
      )}
    </div>
  );
};
